/**
 * @module VideoHandler
 * Unified video handling module for YouTube and private videos
 */
((Drupal, once) => {
  /**
   * Initialize YouTube API if needed
   * @returns {Promise} Promise that resolves when YouTube API is ready
   */
  const initYouTubeAPI = () => {
    return new Promise((resolve) => {
      const youtubeScriptId = 'youtube-api';
      const youtubeScript = document.getElementById(youtubeScriptId);

      // If YouTube API is already loaded
      if (window.YT) {
        resolve();
        return;
      }

      // If YouTube API script is not yet added
      if (!youtubeScript) {
        const tag = document.createElement('script');
        const firstScript = document.getElementsByTagName('script')[0];

        tag.src = 'https://www.youtube.com/iframe_api';
        tag.id = youtubeScriptId;
        firstScript.parentNode.insertBefore(tag, firstScript);
      }

      // Set callback for when API is ready
      window.onYouTubeIframeAPIReady = () => {
        resolve();
      };
    });
  };

  /**
   * Create YouTube player
   * @param {HTMLElement} element - Element to replace with player
   * @param {string} videoId - YouTube video ID
   * @param {Object} options - Player options
   */
  const createYouTubePlayer = (element, videoId, options = {}) => {
    new window.YT.Player(element, {
      videoId,
      playerVars: {
        autoplay: 1,
        modestbranding: 1,
        rel: 0,
      },
      events: {
        onReady: (event) => {
          event.target.setVolume(100);
          event.target.playVideo();
        },
      },
    });
  };

  /**
   * Extract YouTube video ID from URL
   * @param {string} url - YouTube URL
   * @returns {string} YouTube video ID
   */
  const extractYouTubeId = (url) => {
    if (!url) return null;

    // Handle different YouTube URL formats
    let videoId = null;

    if (url.includes('v=')) {
      // Standard YouTube URL: https://www.youtube.com/watch?v=VIDEO_ID
      videoId = url.split('v=')[1];
      // Remove any additional parameters
      const ampersandPosition = videoId.indexOf('&');
      if (ampersandPosition !== -1) {
        videoId = videoId.substring(0, ampersandPosition);
      }
    } else if (url.includes('youtu.be/')) {
      // Short YouTube URL: https://youtu.be/VIDEO_ID
      videoId = url.split('youtu.be/')[1];
    } else if (url.match(/^[a-zA-Z0-9_-]{11}$/)) {
      // Direct video ID
      videoId = url;
    }

    return videoId;
  };

  // YouTube video handling behavior
  Drupal.behaviors.videoHandler = {
    attach: (context) => {
      // Handle standard YouTube videos
      const youtubeVideoPreviews = once(
        'youtube-video-preview',
        '.video-preview',
        context,
      );

      for (const preview of youtubeVideoPreviews) {
        preview.addEventListener('click', async () => {
          const videoUrl = preview.getAttribute('data-video-id');
          const videoId = extractYouTubeId(videoUrl);

          if (!videoId) return;

          const isCyberfitness = preview.classList.contains(
            'block-cyberfitness__video-preview',
          );
          const options = {
            width: isCyberfitness ? preview.parentNode.offsetWidth : '854',
            height: isCyberfitness ? preview.parentNode.offsetHeight : '480',
          };

          await initYouTubeAPI();
          createYouTubePlayer(preview, videoId, options);
        });
      }

      // Handle private videos (non-YouTube)
      const privateVideoPreviews = once(
        'private-video-preview',
        '.paragraph-video-private .video-preview',
        context,
      );

      for (const preview of privateVideoPreviews) {
        preview.addEventListener('click', () => {
          const videoId = preview.getAttribute('data-video-id');

          if (videoId && videoId !== '/video-error') {
            const iframe = document.createElement('iframe');
            iframe.src = videoId;
            iframe.title = videoId;
            iframe.className = 'video-private';
            iframe.allowFullscreen = true;

            // Clear the preview and append the iframe
            while (preview.firstChild) {
              preview.removeChild(preview.firstChild);
            }
            preview.appendChild(iframe);
          }
        });
      }
    },
  };
})(Drupal, once);

// HMR handling
if (import.meta.hot) {
  import.meta.hot.accept(() => console.log('HMR'));
}
